// Marks where a query landed in a piece of text. Cosmetic only: what matches
// is decided by the database (migration 0021), and this never filters
// anything. It folds the way the database does — case, accents, and the
// German umlaut spellings — but keeps a map back to the original characters
// so a mark lands on "Zürich" when the query was "zuerich".

const UMLAUT: Record<string, string> = { ä: "ae", ö: "oe", ü: "ue", Ä: "ae", Ö: "oe", Ü: "ue" };

interface Folded { s: string; from: number[]; to: number[] }

/** Folded text plus, for each folded character, the original span it came from. */
function fold(text: string, german: boolean): Folded {
  let s = "";
  const from: number[] = [];
  const to: number[] = [];
  let i = 0;
  for (const ch of text) {
    const next = i + ch.length;
    const f = (german && UMLAUT[ch]) ||
      ch.normalize("NFD").replace(/\p{M}/gu, "").replace(/ß/g, "ss").toLowerCase();
    for (const c of f) { s += c; from.push(i); to.push(next); }
    i = next;
  }
  return { s, from, to };
}

export const queryTokens = (q: string): string[] =>
  [...new Set([...q.matchAll(/[\p{L}\p{N}]+/gu)].map((m) => m[0].toLowerCase()))].slice(0, 8);

const isWordChar = (c: string | undefined) => !!c && /[\p{L}\p{N}]/u.test(c);

/** Merged [start, end) ranges of `text` where a query word starts a word. */
export function markRanges(text: string, tokens: string[]): [number, number][] {
  if (!text || tokens.length === 0) return [];
  const ranges: [number, number][] = [];
  for (const german of [true, false]) {
    const f = fold(text, german);
    for (const tok of tokens) {
      for (const variant of new Set([fold(tok, true).s, fold(tok, false).s])) {
        if (!variant) continue;
        let at = f.s.indexOf(variant);
        while (at !== -1) {
          if (!isWordChar(f.s[at - 1])) ranges.push([f.from[at], f.to[at + variant.length - 1]]);
          at = f.s.indexOf(variant, at + 1);
        }
      }
    }
  }
  ranges.sort((a, b) => a[0] - b[0] || b[1] - a[1]);
  const merged: [number, number][] = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else merged.push([r[0], r[1]]);
  }
  return merged;
}

/**
 * A short stretch of `text` around the first mark, for a row whose match is
 * in a field the row does not otherwise show. Null when nothing matches.
 */
export function excerpt(text: string, tokens: string[], span = 64): string | null {
  const flat = text.replace(/\s+/g, " ").trim();
  const ranges = markRanges(flat, tokens);
  if (ranges.length === 0) return null;
  // Start just before the match rather than centring on it: a row shows one
  // line and cuts the rest with an ellipsis, so the match has to be early.
  const start = Math.max(0, ranges[0][0] - 12);
  return `${start > 0 ? "…" : ""}${flat.slice(start, start + span)}${start + span < flat.length ? "…" : ""}`;
}

/**
 * The id a query is spelling, in the loose forms the server accepts — used
 * only to mark that id on the row it found. Mirrors `parseId` in the API's
 * search.ts; if the two ever disagree the server is right and this merely
 * fails to mark.
 */
export function idFromQuery(q: string): string | null {
  const m = /^\s*([tf])?\s*[-_.]?\s*0*(\d{1,6})\s*$/i.exec(q);
  if (!m) return null;
  const n = Number(m[2]);
  if (!Number.isSafeInteger(n) || n < 1) return null;
  return m[1]?.toLowerCase() === "f" ? `F${n}` : `T-${String(n).padStart(3, "0")}`;
}
